import React, { useState, useEffect } from "react";
import {
  getProductsPaginated,
  getMarketplaceCategories,
  getFeaturedProducts,
} from "../lib/sanity/client";
import { urlForImage } from "../lib/sanity/client";
import "./AwoofMarketplaceGallery.css";

const AwoofMarketplaceGallery = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const PRODUCTS_PER_PAGE = 12;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, selectedCategory]);

  const fetchInitialData = async () => {
    try {
      const [categoriesData, featuredData] = await Promise.all([
        getMarketplaceCategories(),
        getFeaturedProducts(6),
      ]);

      setCategories(categoriesData);
      setFeaturedProducts(featuredData);
      setInitialLoad(false);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setInitialLoad(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const categorySlug = selectedCategory === "all" ? null : selectedCategory;
      const result = await getProductsPaginated(
        currentPage,
        PRODUCTS_PER_PAGE,
        categorySlug
      );

      setProducts(result.products);
      setTotalPages(result.totalPages);
      setTotalProducts(result.total);
      setHasNext(result.hasNext);
      setHasPrev(result.hasPrev);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Loading Skeleton Component
  const ProductSkeleton = () => (
    <div className="awoof-gallery-product-card awoof-gallery-skeleton">
      <div className="awoof-gallery-skeleton-image"></div>
      <div className="awoof-gallery-product-info">
        <div className="awoof-gallery-skeleton-line awoof-gallery-skeleton-title"></div>
        <div className="awoof-gallery-skeleton-line awoof-gallery-skeleton-description"></div>
        <div className="awoof-gallery-skeleton-line awoof-gallery-skeleton-price"></div>
        <div className="awoof-gallery-skeleton-buttons">
          <div className="awoof-gallery-skeleton-button"></div>
          <div className="awoof-gallery-skeleton-button"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="awoof-gallery-container">
      <header className="awoof-gallery-header">
        <h1>Awoof Marketplace Gallery</h1>
        <p>Discover quality pre-owned items at unbeatable prices</p>
      </header>

      {initialLoad ? (
        <div className="awoof-gallery-initial-loading">
          <div className="awoof-gallery-loading-spinner">
            Loading marketplace...
          </div>
        </div>
      ) : (
        <>
          {/* Featured Products Section */}
          {featuredProducts.length > 0 &&
            currentPage === 1 &&
            selectedCategory === "all" && (
              <section className="awoof-gallery-featured-section">
                <h2>Featured Products</h2>
                <div className="awoof-gallery-featured-grid">
                  {featuredProducts.map((product) => (
                    <ProductCard
                      key={`featured-${product._id}`}
                      product={product}
                      isFeatured={true}
                    />
                  ))}
                </div>
              </section>
            )}

          {/* Category Filters */}
          <div className="awoof-gallery-filters">
            <button
              className={
                selectedCategory === "all" ? "awoof-gallery-filter-active" : ""
              }
              onClick={() => handleCategoryChange("all")}
              disabled={loading}
            >
              All Categories
            </button>
            {categories.map((category) => (
              <button
                key={category._id}
                className={
                  selectedCategory === category.slug?.current
                    ? "awoof-gallery-filter-active"
                    : ""
                }
                onClick={() => handleCategoryChange(category.slug?.current)}
                disabled={loading}
              >
                {category.icon && (
                  <span className="awoof-gallery-category-icon">
                    {category.icon}
                  </span>
                )}
                {category.name}
              </button>
            ))}
          </div>

          {/* Products Count */}
          <div className="awoof-gallery-products-meta">
            <p>
              {loading && currentPage === 1
                ? "Loading products..."
                : `Showing ${products.length} of ${totalProducts} products`}
              {selectedCategory !== "all" && !loading && (
                <span>
                  {" "}
                  in{" "}
                  {
                    categories.find((c) => c.slug?.current === selectedCategory)
                      ?.name
                  }
                </span>
              )}
            </p>
            {loading && currentPage === 1 && (
              <div className="awoof-gallery-loading-indicator">
                <div className="awoof-gallery-mini-spinner"></div>
              </div>
            )}
          </div>

          {/* Products Grid */}
          <div className="awoof-gallery-products-grid">
            {loading && currentPage === 1
              ? // Show skeletons for initial page load
                Array(PRODUCTS_PER_PAGE)
                  .fill(0)
                  .map((_, index) => (
                    <ProductSkeleton key={`skeleton-${index}`} />
                  ))
              : // Show actual products
                products.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}
          </div>

          {/* Loading overlay for page changes (not initial load) */}
          {loading && currentPage > 1 && (
            <div className="awoof-gallery-loading-overlay">
              <div className="awoof-gallery-loading-spinner">
                Loading more products...
              </div>
            </div>
          )}

          {/* No Products Message */}
          {!loading && products.length === 0 && (
            <div className="awoof-gallery-no-products">
              <p>No products found in this category.</p>
              {selectedCategory !== "all" && (
                <button onClick={() => handleCategoryChange("all")}>
                  View All Products
                </button>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && !loading && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              hasNext={hasNext}
              hasPrev={hasPrev}
            />
          )}
        </>
      )}
    </div>
  );
};

// Pagination Component
const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  hasNext,
  hasPrev,
}) => {
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="awoof-gallery-pagination">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={!hasPrev}
        className="awoof-gallery-pagination-btn awoof-gallery-pagination-prev"
      >
        ← Previous
      </button>

      <div className="awoof-gallery-pagination-numbers">
        {getPageNumbers().map((pageNum, index) => (
          <button
            key={index}
            onClick={() => typeof pageNum === "number" && onPageChange(pageNum)}
            className={`awoof-gallery-pagination-btn ${
              pageNum === currentPage ? "awoof-gallery-pagination-active" : ""
            } ${pageNum === "..." ? "awoof-gallery-pagination-dots" : ""}`}
            disabled={pageNum === "..."}
          >
            {pageNum}
          </button>
        ))}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={!hasNext}
        className="awoof-gallery-pagination-btn awoof-gallery-pagination-next"
      >
        Next →
      </button>
    </div>
  );
};

const ProductCard = ({ product, isFeatured = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const productImages =
    product.images && product.images.length > 0
      ? product.images
      : [{ alt: product.title }];

  const currentImageUrl = productImages[currentImageIndex]
    ? urlForImage(productImages[currentImageIndex]).width(400).height(300).url()
    : "/images/placeholder-product.jpg";

  // Calculate savings for both currencies
  const savingsNGN = product.originalValue
    ? product.originalValue - product.price
    : 0;
  const savingsPercentNGN = product.originalValue
    ? Math.round((savingsNGN / product.originalValue) * 100)
    : 0;

  const savingsUSD =
    product.originalValueUSD && product.priceUSD
      ? product.originalValueUSD - product.priceUSD
      : 0;
  const savingsPercentUSD =
    product.originalValueUSD && product.priceUSD
      ? Math.round((savingsUSD / product.originalValueUSD) * 100)
      : 0;

  // Use the higher savings percentage for display
  const savingsPercent = Math.max(savingsPercentNGN, savingsPercentUSD);

  const handleWhatsAppContact = () => {
    let priceText = `₦${product.price?.toLocaleString()}`;
    if (product.priceUSD) {
      priceText += ` ($${product.priceUSD?.toLocaleString()})`;
    }

    const message = `I want to get ${product.title} - ${priceText}`;
    const whatsappUrl = `https://wa.me/2349114351195?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleImageNavigation = (direction) => {
    if (productImages.length <= 1) return;

    if (direction === "next") {
      setCurrentImageIndex((prev) =>
        prev === productImages.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentImageIndex((prev) =>
        prev === 0 ? productImages.length - 1 : prev - 1
      );
    }
  };

  const handleImageDotClick = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <div
      className={`awoof-gallery-product-card ${
        isFeatured ? "awoof-gallery-featured-card" : ""
      }`}
    >
      {product.featured && (
        <div className="awoof-gallery-featured-badge">Featured</div>
      )}

      <div className="awoof-gallery-product-image">
        <img
          src={currentImageUrl}
          alt={productImages[currentImageIndex]?.alt || product.title}
        />

        {/* Multiple Images Navigation */}
        {productImages.length > 1 && (
          <>
            <button
              className="awoof-gallery-image-nav awoof-gallery-image-nav-prev"
              onClick={(e) => {
                e.stopPropagation();
                handleImageNavigation("prev");
              }}
              aria-label="Previous image"
            >
              ‹
            </button>

            <button
              className="awoof-gallery-image-nav awoof-gallery-image-nav-next"
              onClick={(e) => {
                e.stopPropagation();
                handleImageNavigation("next");
              }}
              aria-label="Next image"
            >
              ›
            </button>

            {/* Image Dots Indicator */}
            <div className="awoof-gallery-image-dots">
              {productImages.map((_, index) => (
                <button
                  key={index}
                  className={`awoof-gallery-image-dot ${
                    index === currentImageIndex ? "active" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageDotClick(index);
                  }}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>

            {/* Image Counter */}
            <div className="awoof-gallery-image-counter">
              {currentImageIndex + 1} / {productImages.length}
            </div>
          </>
        )}

        {product.condition && (
          <span
            className={`awoof-gallery-condition-badge ${product.condition
              .toLowerCase()
              .replace(" ", "-")}`}
          >
            {product.condition}
          </span>
        )}
        {savingsPercent > 0 && (
          <span className="awoof-gallery-savings-badge">
            Save {savingsPercent}%
          </span>
        )}
      </div>

      <div className="awoof-gallery-product-info">
        <h3>{product.title}</h3>
        <p className="awoof-gallery-product-description">
          {product.description}
        </p>

        <div className="awoof-gallery-price-section">
          <div className="awoof-gallery-price-group">
            <span className="awoof-gallery-current-price">
              ₦{product.price?.toLocaleString()}
            </span>
            {product.priceUSD && (
              <span className="awoof-gallery-current-price-usd">
                ${product.priceUSD?.toLocaleString()}
              </span>
            )}
          </div>

          {(product.originalValue || product.originalValueUSD) && (
            <div className="awoof-gallery-original-pricing">
              {product.originalValue && (
                <span className="awoof-gallery-original-price">
                  ₦{product.originalValue.toLocaleString()}
                </span>
              )}
              {product.originalValueUSD && (
                <span className="awoof-gallery-original-price">
                  ${product.originalValueUSD.toLocaleString()}
                </span>
              )}
            </div>
          )}
        </div>

        {product.category && (
          <div className="awoof-gallery-product-category">
            <span className="awoof-gallery-category-tag">
              {product.category.name}
            </span>
          </div>
        )}

        {product.tags && product.tags.length > 0 && (
          <div className="awoof-gallery-product-tags">
            {product.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="awoof-gallery-tag">
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="awoof-gallery-product-actions">
          <button
            className="awoof-gallery-btn-primary"
            onClick={() =>
              (window.location.href = `/marketplace/product/${product.slug.current}`)
            }
          >
            View Details
          </button>
          <button
            className="awoof-gallery-btn-secondary"
            onClick={handleWhatsAppContact}
          >
            Contact Us
          </button>
        </div>
      </div>
    </div>
  );
};

export default AwoofMarketplaceGallery;
